import {
  Feature,
  getOrganisationsGrantingProjectAccess,
  hasFeatureForProject,
  RESEARCH_ACCESS_EXCEPTION_PROJECT_ID,
} from '@resistapp/common/features';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { determineResearchAvailableNormalisationModes } from '@resistapp/client/contexts/sample-data-context';
import { shouldSuppressVolumeModesForProject } from '@resistapp/client/contexts/use-overview-context/overview-context-utils';
import { isAyNormalisationMode } from '@resistapp/common/normalisation-mode';
import { ProjectCtsResponse } from '@resistapp/common/api-types';
import { Chip, CtsColumns, DispenserPrefix, FullSamplesByUID, NormalisationMode, User } from '@resistapp/common/types';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { Dictionary, uniq, values } from 'lodash';

type ChipWithDispenserPrefix = Pick<Chip, 'name' | 'dispenserPrefix'>;

const LEGACY_CHIP_DEFAULT_DISPENSER_PREFIX: DispenserPrefix = 'MSND';
const ayResultDownloadOrganisationId = 288;

function normalizeDispenserPrefix(dispenserPrefix: ChipWithDispenserPrefix['dispenserPrefix']): DispenserPrefix {
  return dispenserPrefix ?? LEGACY_CHIP_DEFAULT_DISPENSER_PREFIX;
}

function hasMixedProjectDispenserPrefixes(chips: ChipWithDispenserPrefix[]): boolean {
  const dispenserPrefixes = uniq(chips.map(chip => normalizeDispenserPrefix(chip.dispenserPrefix)));
  return dispenserPrefixes.includes('MSND') && dispenserPrefixes.includes('SCND');
}

export function getMachineLabelByCtsFilename(chips: ChipWithDispenserPrefix[]): Dictionary<string> {
  if (!hasMixedProjectDispenserPrefixes(chips)) {
    return {};
  }
  return chips.reduce<Dictionary<string>>((labelsByFilename, chip) => {
    labelsByFilename[`${chip.name}.txt`] = `(${normalizeDispenserPrefix(chip.dispenserPrefix)})`;
    return labelsByFilename;
  }, {});
}

export function shouldHideResearchOnlyDemoExtras(
  user: Parameters<typeof hasFeatureForProject>[0],
  projectId: number,
): boolean {
  return projectId === RESEARCH_ACCESS_EXCEPTION_PROJECT_ID && !hasFeatureForProject(user, Feature.RESEARCH, projectId);
}

export function canDownloadAyResearchResults(user: User | undefined | null, projectId: number): boolean {
  if (isResistomapAdminUser(user)) {
    return true;
  }

  return getOrganisationsGrantingProjectAccess(user, projectId).some(
    organisation => organisation.id === ayResultDownloadOrganisationId,
  );
}

export function getDownloadableNormalisationModes(
  availableNormalisationModes: NormalisationMode[],
  canDownloadAy: boolean,
): NormalisationMode[] {
  return availableNormalisationModes.filter(mode => !isAyNormalisationMode(mode) || canDownloadAy);
}

export function getProjectDownloadableNormalisationModes(
  samplesByUID: FullSamplesByUID,
  canDownloadAy: boolean,
): NormalisationMode[] {
  const projectSamples = flattenSamplesByUID(samplesByUID);
  const projectAvailableNormalisationModes = determineResearchAvailableNormalisationModes(
    projectSamples,
    shouldSuppressVolumeModesForProject(projectSamples),
  );
  return getDownloadableNormalisationModes(projectAvailableNormalisationModes, canDownloadAy);
}

export function getDetectedGenesDownloadNormalisationMode(
  selectedNormalisationMode: NormalisationMode,
  downloadableNormalisationModes: NormalisationMode[],
): NormalisationMode | undefined {
  return downloadableNormalisationModes.includes(selectedNormalisationMode)
    ? selectedNormalisationMode
    : downloadableNormalisationModes[0];
}

// The downloadable Cq file names, one per project chip (every project chip has Cq rows of a visible sample)
export function getCtsFilenames(chips: Array<Pick<Chip, 'name'>>): string[] {
  return chips.map(chip => `${chip.name}.txt`);
}

export function flattenProjectCtsRows(ctsBySampleId: ProjectCtsResponse['ctsBySampleId']): CtsColumns[] {
  return values(ctsBySampleId).flatMap(ctsByAssay => values(ctsByAssay).flat());
}
